import { useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Send, CheckCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const eventTypes = [
  'Birthday Party',
  'Wedding',
  'Corporate Event',
  'Festival/Fair',
  'School Event',
  'Community Gathering',
  'Other'
];

export const EventsPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    event_date: '',
    event_type: '',
    guest_count: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API}/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          guest_count: parseInt(formData.guest_count)
        })
      });

      if (!response.ok) throw new Error('Failed to submit booking');

      setSubmitted(true);
      toast.success('Booking request submitted!');
    } catch (error) {
      console.error('Booking error:', error);
      toast.error('Failed to submit booking. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="min-h-screen bg-white" data-testid="events-page">
      <Navbar />
      
      {/* Header */}
      <section className="bg-gradient-to-r from-sip-purple to-sip-purple/80 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Calendar className="w-16 h-16 mx-auto mb-4 text-white" />
            <h1 className="font-script text-4xl md:text-6xl text-white mb-4">
              Private Events
            </h1>
            <p className="text-xl text-white/90 max-w-2xl mx-auto">
              Let us bring the party to your next event!
            </p>
          </motion.div>
        </div>
      </section>

      {/* Booking Form */}
      <section className="py-16">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          {submitted ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="card-featured text-center py-12"
              data-testid="booking-success"
            >
              <CheckCircle className="w-20 h-20 mx-auto mb-6 text-sip-purple" />
              <h2 className="font-script text-3xl text-gray-900 mb-4">
                Request Received!
              </h2>
              <p className="text-lg text-gray-600">
                We'll be in touch soon to discuss your event details.
              </p>
              <button
                onClick={() => {
                  setSubmitted(false);
                  setFormData({
                    name: '',
                    email: '',
                    phone: '',
                    event_date: '',
                    event_type: '',
                    guest_count: '',
                    message: ''
                  });
                }}
                className="btn-primary mt-8"
                data-testid="new-booking-btn"
              >
                Submit Another Request
              </button>
            </motion.div>
          ) : (
            <motion.form
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              onSubmit={handleSubmit}
              className="card-graffiti"
              data-testid="booking-form"
            >
              <h2 className="font-script text-3xl text-gray-900 mb-6 text-center">
                Book Us For Your Event
              </h2>

              <div className="space-y-6">
                <div>
                  <label className="block font-medium text-gray-700 mb-2">
                    Your Name *
                  </label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="input-graffiti"
                    placeholder="John Doe"
                    data-testid="input-name"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="input-graffiti"
                      placeholder="john@example.com"
                      data-testid="input-email"
                    />
                  </div>
                  <div>
                    <label className="block font-medium text-gray-700 mb-2">
                      Phone *
                    </label>
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="input-graffiti"
                      placeholder="(555) 123-4567"
                      data-testid="input-phone"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block font-medium text-gray-700 mb-2">
                      Event Date *
                    </label>
                    <Input
                      type="date"
                      name="event_date"
                      value={formData.event_date}
                      onChange={handleChange}
                      required
                      className="input-graffiti"
                      data-testid="input-date"
                    />
                  </div>
                  <div>
                    <label className="block font-medium text-gray-700 mb-2">
                      Expected Guests *
                    </label>
                    <Input
                      type="number"
                      name="guest_count"
                      value={formData.guest_count}
                      onChange={handleChange}
                      required
                      min="1"
                      className="input-graffiti"
                      placeholder="50"
                      data-testid="input-guests"
                    />
                  </div>
                </div>

                <div>
                  <label className="block font-medium text-gray-700 mb-2">
                    Event Type *
                  </label>
                  <select
                    value={formData.event_type}
                    onChange={(e) => setFormData(prev => ({ ...prev, event_type: e.target.value }))}
                    required
                    className="input-graffiti w-full cursor-pointer"
                    data-testid="select-event-type"
                  >
                    <option value="">Select event type</option>
                    {eventTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block font-medium text-gray-700 mb-2">
                    Additional Details
                  </label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    className="input-graffiti min-h-[120px]"
                    placeholder="Tell us about your event, any special requests, etc."
                    data-testid="input-message"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                  data-testid="submit-booking-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="animate-spin" size={20} />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send size={20} />
                      Submit Request
                    </>
                  )}
                </button>
              </div>
            </motion.form>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EventsPage;
